import { DashboardCard } from "@dashboard/components/Card";
import Date from "@dashboard/components/Date";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import { TableButtonWrapper } from "@dashboard/components/TableButtonWrapper/TableButtonWrapper";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination";
import TableRowLink from "@dashboard/components/TableRowLink";
import { customerUrl } from "@dashboard/customers/urls";
import { getUserName } from "@dashboard/misc";
import { ListProps } from "@dashboard/types";
import { TableBody, TableCell, TableFooter, TableHead } from "@material-ui/core";
import { Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import { FC } from "react";
import { FormattedMessage } from "react-intl";

import { NewsletterSubscriber } from "../../types";
import { messages } from "../NewsletterListPage/messages";

interface NewsletterSubscribersListProps
  extends Pick<ListProps, "disabled" | "settings" | "onUpdateListSettings"> {
  subscribers: NewsletterSubscriber[] | undefined;
  onUnsubscribe: (subscriber: NewsletterSubscriber) => void;
}

const numberOfColumns = 4;

export const NewsletterSubscribersList: FC<NewsletterSubscribersListProps> = ({
  subscribers,
  disabled,
  settings,
  onUpdateListSettings,
  onUnsubscribe,
}) => (
  <DashboardCard>
    <DashboardCard.Header>
      <DashboardCard.Title>
        <FormattedMessage {...messages.subscribersTitle} />
      </DashboardCard.Title>
      <DashboardCard.Subtitle>
        <FormattedMessage {...messages.subscribersSubtitle} />
      </DashboardCard.Subtitle>
    </DashboardCard.Header>
    <ResponsiveTable data-test-id="newsletter-subscribers-list">
      <TableHead>
        <TableRowLink>
          <TableCell>
            <FormattedMessage {...messages.colName} />
          </TableCell>
          <TableCell>
            <FormattedMessage {...messages.colEmail} />
          </TableCell>
          <TableCell>
            <FormattedMessage {...messages.colJoined} />
          </TableCell>
          <TableCell>
            <FormattedMessage {...messages.colActions} />
          </TableCell>
        </TableRowLink>
      </TableHead>
      <TableFooter>
        <TableRowLink>
          <TablePaginationWithContext
            colSpan={numberOfColumns}
            settings={settings}
            disabled={disabled}
            onUpdateListSettings={onUpdateListSettings}
          />
        </TableRowLink>
      </TableFooter>
      <TableBody>
        {subscribers === undefined ? (
          <TableRowLink>
            <TableCell colSpan={numberOfColumns}>
              <Skeleton />
            </TableCell>
          </TableRowLink>
        ) : subscribers.length === 0 ? (
          <TableRowLink data-test-id="empty-list-message">
            <TableCell colSpan={numberOfColumns}>
              <FormattedMessage {...messages.noSubscribers} />
            </TableCell>
          </TableRowLink>
        ) : (
          subscribers.map(subscriber => (
            <TableRowLink
              key={subscriber.id}
              href={customerUrl(subscriber.id)}
              hover
              data-test-id={"subscriber-" + subscriber.id}
            >
              <TableCell>
                <Text>{getUserName(subscriber, true)}</Text>
              </TableCell>
              <TableCell>
                <Text>{subscriber.email}</Text>
              </TableCell>
              <TableCell>
                {subscriber.dateJoined ? <Date date={subscriber.dateJoined} /> : "-"}
              </TableCell>
              <TableCell>
                <TableButtonWrapper>
                  <Button
                    variant="secondary"
                    disabled={disabled}
                    onClick={() => onUnsubscribe(subscriber)}
                    data-test-id={"unsubscribe-" + subscriber.id}
                  >
                    <FormattedMessage {...messages.unsubscribe} />
                  </Button>
                </TableButtonWrapper>
              </TableCell>
            </TableRowLink>
          ))
        )}
      </TableBody>
    </ResponsiveTable>
  </DashboardCard>
);
